<script setup lang="ts">
import { reactive, ref } from 'vue'
import type { Form, FormError, FormSubmitEvent } from '#ui/types'
import { z } from 'zod'

const config = useRuntimeConfig()

const props = defineProps({
  userType: {
    userType: String,
    default: 'candidate',
    enums: ['candidate', 'startup']
  }
})

const { $pb } = useNuxtApp()

const toast = useToast()

const verifyEmail = ref(false)

interface State {
  name: string;
  surname: string;
  email: string;
  password: string;
  passwordConfirm: string;
}

const state: State = reactive({
  name: '',
  surname: '',
  email: '',
  password: '',
  passwordConfirm: ''
})

const loading = ref(false)
const signUpForm = ref<Form<State> | null>(null)

const provider = ref('')

onMounted(() => {
  if ($pb.authStore.isValid) {
    navigateTo('/profile')
  }
})

const providers = [{
  label: 'Continue with Google',
  icon: 'i-logos-google-icon',
  color: 'neutral' as const,
  onClick: async () => {
    try {
      loading.value = true
      provider.value = 'Google'

      const state = btoa(JSON.stringify({
        userType: props.userType || 'candidate',
        timestamp: Date.now()
      }))

      await $pb.collection('users').authWithOAuth2({
        provider: 'google',
        query: {
          state: state
        }
      })

      if ($pb.authStore.token) {
        await $pb.collection('users').update($pb.authStore.record?.id as string, {
          active: true,
          userType: props.userType || 'candidate',
          agreeTCs: true,
          jobNotifications: true,
          marketingConsent: true,
          emailVisibility: true
        })
        navigateTo(props.userType.includes('candidate') ? '/profile' : '/admin/startups')
      }
    } catch (error: any) {
      if (error.message.startsWith('This email is already registered')) {
        toast.add({
          title: 'Authentication Failed',
          description: error.message,
          icon: 'i-heroicons-exclamation-triangle',
          color: 'error',
          duration: 10000
        })
        navigateTo('/sign-in')

        toast.add({
          title: 'Note',
          description: 'We have redirected you to the sign in page',
          icon: 'i-heroicons-information-circle',
          color: 'info',
          duration: 10000
        })
        return
      }

      toast.add({
        title: 'Authentication Failed',
        description: error.message,
        icon: 'i-heroicons-exclamation-triangle',
        color: 'error',
        duration: 10000
      })
    } finally {
      loading.value = false
      provider.value = ''
    }
  }
}]

let validationMessagesServer: any = {}

const schema = z.object({
  name: z.string().min(1),
  email: z.string().email().refine((email) => {
    if (props.userType === 'startup' && forbiddenEmails.some(domain => email.includes(domain))) {
      return false
    }
    return true
  }, {
    message: 'Startups cannot use free email providers to sign up'
  }),
  password: z.string().min(8),
  passwordConfirm: z.string().min(8)
}).superRefine((data, ctx) => {
  if (data.passwordConfirm !== data.password) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Your passwords don't match",
      path: ['passwordConfirm']
    });
  }
});

type Schema = z.infer<typeof schema>

const fields = [{
  name: 'name',
  type: 'text' as const,
  label: 'Full Name',
  placeholder: 'Enter your full name',
  required: true
}, {
  name: 'email',
  type: 'text' as const,
  label: 'Email',
  placeholder: 'Enter your email',
  required: true
}, {
  name: 'password',
  label: 'Password',
  type: 'password' as const,
  placeholder: 'Enter your password'
}, {
  name: 'passwordConfirm',
  label: 'Confirm Password',
  type: 'password' as const,
  placeholder: 'Confirm your password'
}]


const doSignUp = async (event: FormSubmitEvent<Schema>) => {
  loading.value = true

  toast.add({
    title: 'Sign Up',
    description: 'Attempting to sign you up',
    icon: 'i-heroicons-lock-closed',
    color: 'info'
  })

  const fromData = {
    name: event.data.name,
    email: event.data.email,
    password: event.data.password,
    passwordConfirm: event.data.passwordConfirm
  }

  try {
    const user = await $pb.collection('users').create(fromData)

    toast.add({
      title: 'Sign Up Successful',
      description: 'You have been signed up',
      icon: 'i-heroicons-check-circle',
      color: 'success'
    })

    await $pb.collection('users').authWithPassword(String(event.data.email), String(event.data.password))

    verifyEmail.value = true

    if ($pb.authStore.token) {
      await $pb.collection('users').update($pb.authStore.record?.id as string, {
        active: true,
        userType: props.userType || 'candidate',
        agreeTCs: true,
        jobNotifications: true,
        marketingConsent: true,
        emailVisibility: true
      })
    }
    navigateTo('/verify-email')
  } catch (error: any) {
    console.log(error)
    let message = 'Sorry, there was an error creating your account, please try again or contact support.'

    if (error?.response?._data?.email) {
      message = 'You may already have an account, try resetting your password.'
    }
    toast.add({
      title: 'Error',
      description: message,
      icon: 'i-heroicons-exclamation-triangle',
      color: 'error',
      duration: 8000
    })
  } finally {
    loading.value = false
  }
}

</script>

<template>
  <div class="relative flex flex-col items-center justify-center w-full min-h-[calc(100vh-100px)]">
    <div
      v-if="loading && provider !== ''"
      class="absolute top-0 left-0 w-full h-full bg-slate-600/40 z-10 flex justify-center items-center text-4xl"
    >
      Waiting for you to authorize with {{ provider }}...
    </div>

    <UCard v-if="!verifyEmail" class="w-full max-w-sm divide-y">
      <UAuthForm
        ref="signUpForm"
        :state="state"
        :schema="schema"
        :fields="fields"
        :providers="providers"
        :submit="{ label: 'Create account' }"
        title="Create an account"
        class="w-full max-w-sm space-y-6"
        @submit="doSignUp"
      >
        <template #title>
          <div class="brunson text-3xl tracking-wider">
            Create an account
          </div>
        </template>

        <template #description>
          Already have an account?
          <ULink
            to="/sign-in"
            class="text-(--ui-primary) font-medium"
          >
            Sign In
          </ULink>.
        </template>

        <template #password-hint>
          <ULink
            to="/forgot-password"
            class="text-(--ui-primary) font-medium"
          >
            Forgot password?
          </ULink>
        </template>

        <template #footer>
          By signing in, you agree to our
          <ULink
            to="/legal/terms-of-service"
            class="text-(--ui-primary) font-medium"
          >
            Terms of Service
          </ULink>.
        </template>
      </UAuthForm>
    </UCard>

    <UCard v-else class="w-full max-w-sm divide-y">
      <VerifyEmail :email="state.email" />
    </UCard>
  </div>
</template>
